import React, { useState } from 'react';

const ENV = process.env.REACT_APP_ENV;
// Preferred: SHA-256 of the access code, baked at build time. The plaintext
// code never reaches the bundle, so it cannot be read out of the JS.
const ACCESS_HASH = process.env.REACT_APP_STAGING_ACCESS_HASH;
// Legacy: plaintext code (only reachable if a deploy still sets it).
const ACCESS_CODE = process.env.REACT_APP_STAGING_ACCESS_CODE;
const STORAGE_KEY = 'staging_auth';

// Computed at module load so the decision is a build-time constant: production
// bundles never mount the gate (no first-paint flash), staging bundles render
// the gate on the very first frame.
export const isStagingGateRequired = Boolean(
    ENV?.startsWith('staging') && (ACCESS_HASH || ACCESS_CODE)
);

const sha256Hex = async (value: string): Promise<string> => {
    const digest = await window.crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(value)
    );
    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
};

const expectedHash = async (): Promise<string | null> => {
    if (ACCESS_HASH) return ACCESS_HASH.toLowerCase();
    if (ACCESS_CODE) return sha256Hex(ACCESS_CODE);
    return null;
};

const readStoredToken = (): string | null => {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

interface StagingGateProps {
    children: React.ReactNode;
}

export const StagingGate: React.FC<StagingGateProps> = ({ children }) => {
    const [isAuthorized, setIsAuthorized] = useState<boolean>(() => {
        if (!isStagingGateRequired) return true;
        const stored = readStoredToken();
        if (!stored) return false;
        if (ACCESS_HASH) return stored === ACCESS_HASH.toLowerCase();
        // Legacy sessions authorized against a plaintext code.
        return Boolean(ACCESS_CODE && stored === ACCESS_CODE);
    });
    const [inputCode, setInputCode] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (verifying) return;
        setVerifying(true);
        setError(false);
        try {
            const expected = await expectedHash();
            const actual = await sha256Hex(inputCode.trim());
            if (expected && actual === expected) {
                try {
                    // Store the hash, never the code itself.
                    localStorage.setItem(STORAGE_KEY, expected);
                } catch {
                    // storage unavailable (private mode) - authorize in-memory
                }
                setIsAuthorized(true);
            } else {
                setError(true);
            }
        } catch {
            // WebCrypto unavailable (insecure context) - refuse rather than open
            setError(true);
        } finally {
            setVerifying(false);
        }
    };

    // Render children if authorized
    if (isAuthorized) {
        return <>{children}</>;
    }

    // Render Gate UI
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-xl overflow-hidden">
                <div className="bg-gray-800 p-6 text-center">
                    <h2 className="text-xl font-bold text-white">Staging Environment</h2>
                    <p className="text-gray-400 text-sm mt-1">Authorized Access Only</p>
                </div>
                <div className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Access Code
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={inputCode}
                                    onChange={(e) => {
                                        setInputCode(e.target.value);
                                        setError(false);
                                    }}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                                    placeholder="Enter code..."
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {error && (
                                <p className="mt-2 text-sm text-red-600">
                                    Incorrect access code
                                </p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={verifying}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-60"
                        >
                            {verifying ? 'Verifying…' : 'Enter Staging'}
                        </button>
                    </form>
                    <div className="mt-6 text-center text-xs text-gray-400">
                        Restricted System • IP Logged
                    </div>
                </div>
            </div>
        </div>
    );
};
